import { useState, useEffect, useRef } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';

function getChartColors() {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  return {
    background: isDark ? '#0f1117' : '#ffffff',
    text: isDark ? '#8b8fa3' : '#6b7080',
    grid: isDark ? '#1e2030' : '#e2e4e9',
    upColor: '#22c55e',
    downColor: '#ef4444',
    wickUpColor: '#22c55e',
    wickDownColor: '#ef4444',
  };
}
const CHART_COLORS = getChartColors();

/**
 * Standalone pop-out chart page for a single timeframe.
 * Receives data via BroadcastChannel. Does NOT use React Router or App shell.
 */
export default function PopoutChartPage() {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const [tf, setTf] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const timeframe = params.get('tf') || '';
    setTf(timeframe);
    document.title = `${timeframe} — Tradrin`;

    // Create chart
    const container = containerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight,
      layout: {
        background: { color: CHART_COLORS.background },
        textColor: CHART_COLORS.text,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: CHART_COLORS.grid },
        horzLines: { color: CHART_COLORS.grid },
      },
      crosshair: { mode: 0 },
      rightPriceScale: { borderColor: CHART_COLORS.grid },
      timeScale: { borderColor: CHART_COLORS.grid, timeVisible: true, secondsVisible: false },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: CHART_COLORS.upColor,
      downColor: CHART_COLORS.downColor,
      borderUpColor: CHART_COLORS.upColor,
      borderDownColor: CHART_COLORS.downColor,
      wickUpColor: CHART_COLORS.wickUpColor,
      wickDownColor: CHART_COLORS.wickDownColor,
    });

    chartRef.current = chart;
    seriesRef.current = series;

    // Resize
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        chart.applyOptions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    resizeObserver.observe(container);

    // Listen for data from parent
    const channel = new BroadcastChannel('tradrin-tf-sync');
    channel.onmessage = (e) => {
      if (e.data.tf === timeframe && e.data.candles && seriesRef.current) {
        seriesRef.current.setData(e.data.candles);
      }
    };

    // Notify parent we're ready
    channel.postMessage({ type: 'popout-ready', tf: timeframe });

    // Notify parent on close
    const handleUnload = () => {
      channel.postMessage({ type: 'popout-closed', tf: timeframe });
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      resizeObserver.disconnect();
      channel.close();
      chart.remove();
    };
  }, []);

  return (
    <div style={{ background: 'var(--bg-primary)', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: '0.4rem 0.75rem', background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-color)', color: 'var(--text-primary)',
        fontSize: '0.9rem', fontWeight: 700, flexShrink: 0,
      }}>
        {tf}
      </div>
      <div ref={containerRef} style={{ flex: 1, minHeight: 0 }} />
    </div>
  );
}
